import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Notification, NotificationResponse } from '../shared/models/notification.model';
import { WebSocketService } from '../shared/services/websocket.service';

@Injectable({
  providedIn: 'root'
})
export class DataSharingService {

  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private wsConnectionSubject = new BehaviorSubject<boolean>(false);
  wsConnected$ = this.wsConnectionSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: WebSocketService
  ) {}

  // ================= REST APIs =================

 getUserProfile(): Observable<any> {
    const headers = new HttpHeaders()
      .set('accept', '*/*');

    return this.http.get<any>(`${environment.baseUrl}v1/auth/profile`, { headers });
  }

  // Get Salesforce account details
  getSalesforceAccountDetails(oppId: string): Observable<any> {
    const headers = new HttpHeaders()
      .set('accept', 'application/json');

    return this.http.get<any>(`${environment.baseUrl}v1/salesforce/account-details/${oppId}`, { headers });
  }

  // Get dashboard metrics
  getDashboardMetrics(oppId: string): Observable<any> {
    const headers = new HttpHeaders()
      .set('accept', 'application/json');

    return this.http.get<any>(`${environment.baseUrl}v1/financelogix/dashboard-metrics/${oppId}`, { headers });
  }

  // Get eligible offers
  getEligibleOffers(flxId: string, oppId: string): Observable<any> {
    const headers = new HttpHeaders()
      .set('accept', 'application/json');

    return this.http.get<any>(`${environment.baseUrl}v1/financelogix/getEligibleOffers/${flxId}/${oppId}`, { headers });
  }

  getNotifications(
    quickView: boolean = true
  ): Observable<NotificationResponse> {
    const oppId =window.localStorage.getItem("oppId")||"006a012354";
    const params = new HttpParams().set('quickView', quickView.toString());

    return this.http
      .get<NotificationResponse>(`${environment.baseUrl}v1/notifications/${oppId}`, { params })
      .pipe(
        tap(response => {
          if (response.status === 'success' && response.data) {
            this.notificationsSubject.next(response.data.notifications);
            this.unreadCountSubject.next(response.data.unreadNotificationCount);
          }
        })
      );
  }

  markNotificationAsRead(notificationId: number): Observable<any> {
    return this.http
      .put(`${environment.baseUrl}v1/notifications/${notificationId}/markAsRead`, {})
      .pipe(
        tap(() => {
          const updated = this.notificationsSubject.value.map(n =>
            n.id === notificationId ? { ...n, read: true } : n
          );

          this.notificationsSubject.next(updated);
          this.unreadCountSubject.next(updated.filter(n => !n.read).length);
        })
      );
  }

  clearNotifications(): void {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  // ================= WEBSOCKET =================

  /**
   * Call ONCE after login / app bootstrap
   */
  initializeSocket(token: string): void {
    const oppId = window.localStorage.getItem("oppId") || "006a012354";
    
    // Connect to WebSocket
    this.socketService.connect();
    
    // Subscribe to oppId after connection
    this.socketService.connected$.subscribe(connected => {
      this.wsConnectionSubject.next(connected);
      
      if (connected) {
        // Subscribe to notifications for this oppId
        this.socketService.subscribe(oppId);
      }
    });

    // Handle notification events
    this.socketService.onNotification((eventType: string, notification: Notification) => {
      console.log(`New notification ${eventType} via WebSocket:`, notification);
      
      if (eventType === 'notification_created' || eventType === 'notification_updated') {
        // Refresh notifications list
        this.getNotifications(true).subscribe();
      }
    });

        this.socketService.onNotificationRead((notificationId: number) => {
      const updated = this.notificationsSubject.value.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      );

      this.notificationsSubject.next(updated);
      this.unreadCountSubject.next(updated.filter(n => !n.read).length);
    });
  }

}
